import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import MainSidebar from "components/MainSidebar";
import Icon from "components/AppIcon";
import Button from "components/ui/Button";
import SummaryCards from "./components/SummaryCards";
import HoldingsTable from "./components/HoldingsTable";
import PerformanceChart from "./components/PerformanceChart";
import SectorAllocation from "./components/SectorAllocation";
import MarginPositions from "./components/MarginPositions";
import DividendTracker from "./components/DividendTracker";
import FilterBar from "./components/FilterBar";
import TradeModal from "./components/TradeModal";
import { supabase } from "../../lib/supabase";
import { useAuth } from "../../contexts/AuthContext";
import { useMultipleRealtimeSubscriptions } from "../../hooks/useRealtimeSubscription";
import { useRealtimePortfolio } from '../../hooks/useRealtimePortfolio';
import { recordFetch } from '../../lib/dataFreshnessTracker';
import { SkeletonCard, ChartSkeleton, SkeletonRow, EmptyState } from 'components/SkeletonLoader';


const getSectorColor = (sector) => {
  const colors = {
    Tech: '#00D9FF', Technology: '#00D9FF', Retail: '#FFB800',
    Manufacturing: '#94A3B8', Financial: '#10B981', Finance: '#10B981',
    BlackMarket: '#EF4444', 'Black Market': '#EF4444',
    Energy: '#F59E0B', Pharma: '#8B5CF6', Defense: '#6366F1',
    Media: '#EC4899', Transport: '#14B8A6',
  };
  return colors?.[sector] || '#64748B';
};

const USER_STATS = { username: "CryptoKing99", level: 24, energy: 68, maxEnergy: 100, xp: 18400, xpToNext: 25000 };

const NOTIFICATION_COUNTS = { messages: 3, alerts: 5, trades: 2 };

// ── Page ──────────────────────────────────────────────────────────────────────
const Portfolio = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [sector, setSector] = useState("All");
  const [sortKey, setSortKey] = useState("value");
  const [tradeModal, setTradeModal] = useState({ open: false, holding: null, action: "buy" });
  const [holdings, setHoldings] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [recentTrades, setRecentTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [realtimeStatus, setRealtimeStatus] = useState('idle');
  const [notification, setNotification] = useState(null);
  const [fetchError, setFetchError] = useState(null);

  const fetchPortfolio = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    setFetchError(null);
    try {
      const [holdingsRes, statsRes, tradesRes] = await Promise.all([
        supabase?.from('portfolio_holdings')?.select('*, stocks(ticker, company_name, sector, current_price, dividend_yield)')?.eq('user_id', user?.id)?.gt('quantity', 0),
        supabase?.from('player_stats')?.select('cash, net_worth')?.eq('user_id', user?.id)?.single(),
        supabase?.from('trade_history')?.select('*, stocks(ticker)')?.eq('user_id', user?.id)?.order('created_at', { ascending: false })?.limit(20),
      ]);

      const rawHoldings = holdingsRes?.data || [];
      const totalPortfolioValue = rawHoldings?.reduce((s, h) => {
        const price = parseFloat(h?.stocks?.current_price || 0);
        return s + (parseInt(h?.quantity) * price);
      }, 0);

      const mapped = rawHoldings?.map(h => {
        const currentPrice = parseFloat(h?.stocks?.current_price || 0);
        const qty = parseInt(h?.quantity);
        const avgCost = parseFloat(h?.avg_buy_price || 0);
        const value = qty * currentPrice;
        const unrealizedPnl = (currentPrice - avgCost) * qty;
        const unrealizedPnlPct = avgCost > 0 ? ((currentPrice - avgCost) / avgCost) * 100 : 0;
        const allocation = totalPortfolioValue > 0 ? (value / totalPortfolioValue) * 100 : 0;
        return {
          id: h?.id,
          ticker: h?.stocks?.ticker || 'N/A',
          sector: h?.stocks?.sector || 'Unknown',
          qty, avgCost, currentPrice, value,
          unrealizedPnl, unrealizedPnlPct, allocation,
          realizedPnl: parseFloat(h?.realized_pnl || 0),
          dividendYield: parseFloat(h?.stocks?.dividend_yield || 0),
          color: getSectorColor(h?.stocks?.sector),
        };
      });

      setHoldings(mapped);
      setRecentTrades(tradesRes?.data || []);
      recordFetch('portfolio_positions');

      const cash = parseFloat(statsRes?.data?.cash || 0);
      const netWorth = parseFloat(statsRes?.data?.net_worth || 0);
      const totalPnl = mapped?.reduce((s, h) => s + h?.unrealizedPnl, 0);
      setMetrics({
        totalValue: totalPortfolioValue,
        cash,
        netWorth,
        dailyChange: totalPnl,
        dailyChangePct: totalPortfolioValue > 0 ? (totalPnl / totalPortfolioValue) * 100 : 0,
        overallReturn: 0,
        overallReturnAbs: totalPnl,
        realizedPnl: mapped?.reduce((s, h) => s + h?.realizedPnl, 0),
        marginUtilization: 0,
        marginUsed: 0,
        marginLimit: 100000,
      });
    } catch (err) {
      console.error('Portfolio fetch error:', err);
      setFetchError(err);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => { fetchPortfolio(); }, [fetchPortfolio]);

  // ── Real-time portfolio hook ───────────────────────────────
  const handleHoldingChange = useCallback((payload) => {
    console.log('[Portfolio] Holding change received:', payload?.eventType);
    fetchPortfolio();
  }, [fetchPortfolio]);

  const handleStatsChange = useCallback((payload) => {
    const s = payload?.new;
    if (!s) return;
    console.log('[Portfolio] Stats update received: cash =', s?.cash);
    setMetrics(prev => prev ? {
      ...prev,
      cash: parseFloat(s?.cash || prev?.cash),
      netWorth: parseFloat(s?.net_worth || prev?.netWorth),
    } : prev);
  }, []);

  const {
    status: rtPortfolioStatus,
    isSyncing,
    isConnected: portfolioConnected,
    lastSyncLabel,
    subscribe: subscribePortfolio,
    forceReconnect: forcePortfolioReconnect,
  } = useRealtimePortfolio(user?.id, handleHoldingChange, handleStatsChange, { enabled: !!user?.id });

  // ── Real-time subscriptions (legacy — disabled) ────────────
  const { statuses, isAllConnected } = useMultipleRealtimeSubscriptions([]);

  useEffect(() => {
    setRealtimeStatus(rtPortfolioStatus);
  }, [rtPortfolioStatus]);

  const enrichedHoldings = holdings;

  const sectorData = holdings?.reduce((acc, h) => {
    const existing = acc?.find(s => s?.name === h?.sector);
    if (existing) existing.value += h?.allocation;
    else acc?.push({ name: h?.sector, value: h?.allocation });
    return acc;
  }, []);

  const filteredHoldings = sector === "All"
    ? enrichedHoldings
    : enrichedHoldings?.filter(h => h?.sector === sector);

  const sortedHoldings = [...filteredHoldings]?.sort((a, b) => {
    if (sortKey === "value") return b?.value - a?.value;
    if (sortKey === "pnl") return b?.unrealizedPnl - a?.unrealizedPnl;
    if (sortKey === "pnlPct") return b?.unrealizedPnlPct - a?.unrealizedPnlPct;
    return 0;
  });

  const handleTrade = useCallback((holding, action) => {
    setTradeModal({ open: true, holding, action });
  }, []);

  const handleTradeConfirm = useCallback((order) => {
    setNotification({ type: "success", msg: `${order?.action === "buy" ? "Buy" : "Sell"} order placed: ${order?.qty} × ${order?.holding?.ticker} @ $${order?.price?.toFixed(2)}` });
    setTimeout(() => setNotification(null), 4000);
  }, []);

  const handleMarginClose = useCallback((pos) => {
    setNotification({ type: "warning", msg: `Closing margin position: ${pos?.ticker} (${pos?.direction})` });
    setTimeout(() => setNotification(null), 4000);
  }, []);

  const handleReinvest = useCallback((div) => {
    setNotification({ type: "success", msg: `Dividend reinvestment enabled for ${div?.ticker}` });
    setTimeout(() => setNotification(null), 4000);
  }, []);

  const handleExport = () => {
    setNotification({ type: "success", msg: "Portfolio data exported to CSV" });
    setTimeout(() => setNotification(null), 3000);
  };

  const contentPadding = sidebarCollapsed ? "lg:pl-[72px]" : "lg:pl-[280px]";

  return (
    <div className="min-h-screen" style={{ background: "var(--color-background)" }}>
      <MainSidebar
        isCollapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed(c => !c)}
        userRole="player"
        notificationCounts={NOTIFICATION_COUNTS}
        userStats={USER_STATS}
      />
      {/* Main Content */}
      <div className={`transition-all duration-250 ${contentPadding} pb-20 lg:pb-8`}>
        {/* Top Bar */}
        <div className="sticky top-0 z-40 border-b border-border px-4 md:px-6 lg:px-8 py-3 flex items-center justify-between" style={{ background: "var(--color-background)" }}>
          <div className="flex items-center gap-3 pl-12 lg:pl-0">
            <Icon name="PieChart" size={20} color="var(--color-primary)" />
            <h1 className="font-heading text-lg md:text-xl font-bold text-foreground">Portfolio</h1>
            <span className="hidden sm:inline font-caption text-xs px-2 py-0.5 rounded" style={{ background: "rgba(0,217,255,0.1)", color: "var(--color-primary)" }}>
              {enrichedHoldings?.length} Positions
            </span>
            {/* Realtime sync badge */}
            {isSyncing ? (
              <span className="flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold"
                style={{ background: 'rgba(251,191,36,0.1)', border: '1px solid rgba(251,191,36,0.25)', color: '#FBBF24' }}>
                <svg className="animate-spin" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 12a9 9 0 11-6.219-8.56" strokeLinecap="round" />
                </svg>
                Syncing...
              </span>
            ) : portfolioConnected ? (
              <span className="flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold"
                style={{ background: 'rgba(52,211,153,0.1)', border: '1px solid rgba(52,211,153,0.25)', color: '#34D399' }}>
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                {lastSyncLabel || 'Live'}
              </span>
            ) : null}
          </div>
          <div className="flex items-center gap-2">
            {!portfolioConnected && (
              <button
                onClick={() => forcePortfolioReconnect(fetchPortfolio)}
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded text-xs font-medium transition-colors hover:opacity-80"
                style={{ background: 'rgba(124,58,237,0.15)', border: '1px solid rgba(124,58,237,0.3)', color: '#A78BFA' }}
              >
                <Icon name="RefreshCw" size={11} />
                Reconnect
              </button>
            )}
            <Button variant="outline" size="sm" iconName="RefreshCw" iconPosition="left" onClick={() => fetchPortfolio()}>
              <span className="hidden sm:inline">Refresh</span>
            </Button>
            <Button variant="default" size="sm" iconName="BarChart2" iconPosition="left" onClick={() => navigate("/stock-exchange")}>
              <span className="hidden sm:inline">Trade</span>
            </Button>
          </div>
        </div>

        {/* Notification Toast */}
        {notification && (
          <div className={`fixed top-16 right-4 z-50 flex items-center gap-3 px-4 py-3 rounded-lg border shadow-lg max-w-sm transition-all duration-300 ${notification?.type === "success" ? "border-green-500/30 bg-green-500/10" : "border-amber-500/30 bg-amber-500/10"}`}>
            <Icon name={notification?.type === "success" ? "CheckCircle" : "AlertTriangle"} size={16} color={notification?.type === "success" ? "var(--color-success)" : "var(--color-warning)"} />
            <span className="font-caption text-xs text-foreground">{notification?.msg}</span>
            <button onClick={() => setNotification(null)} className="ml-auto">
              <Icon name="X" size={14} color="var(--color-muted-foreground)" />
            </button>
          </div>
        )}

        <div className="px-4 md:px-6 lg:px-8 py-6 space-y-6">
          {/* Summary Cards */}
          <SummaryCards metrics={metrics} />

          {/* Charts Row */}
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            <div className="xl:col-span-2">
              {loading ? <ChartSkeleton /> : <PerformanceChart />}
            </div>
            <div className="xl:col-span-1">
              {loading ? <SkeletonCard /> : <SectorAllocation sectors={sectorData} />}
            </div>
          </div>

          {/* Filter Bar + Holdings */}
          <div className="space-y-4">
            <FilterBar
              sector={sector}
              onSectorChange={setSector}
              sortKey={sortKey}
              onSortChange={setSortKey}
              onExport={handleExport}
            />
            {loading ? (
              <SkeletonRow />
            ) : holdings?.length === 0 ? (
              <EmptyState
                icon="Wallet"
                title="No holdings yet"
                description="Start adding positions to your portfolio to see them here."
                actionLabel="Add Position"
                onAction={() => navigate("/stock-exchange")}
                action={() => navigate("/stock-exchange")}
              />
            ) : (
              <HoldingsTable holdings={sortedHoldings} onTrade={handleTrade} loading={loading} />
            )}
          </div>

          {/* Margin Positions */}
          <MarginPositions positions={[]} onClose={handleMarginClose} />

          {/* Dividend Tracker */}
          <DividendTracker dividends={[]} onReinvest={handleReinvest} />

          {/* Quick Nav */}
          <div className="rounded-xl border border-border p-4 md:p-6" style={{ background: "var(--color-card)" }}>
            <div className="flex items-center gap-2 mb-4">
              <Icon name="Zap" size={16} color="var(--color-accent)" />
              <h3 className="font-heading text-sm font-semibold text-foreground">Quick Navigation</h3>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
              {[
                { label: "Dashboard", icon: "LayoutDashboard", path: "/dashboard" },
                { label: "Stock Exchange", icon: "BarChart2", path: "/stock-exchange" },
                { label: "Stock Detail", icon: "TrendingUp", path: "/stock-detail" },
                { label: "Businesses", icon: "Building2", path: "/businesses" },
                { label: "Crime Hub", icon: "Skull", path: "/crime-hub" },
              ]?.map(nav => (
                <button
                  key={nav?.path}
                  onClick={() => navigate(nav?.path)}
                  className="flex flex-col items-center gap-2 p-3 rounded-lg border border-border hover:border-primary/40 hover:bg-primary/5 transition-all duration-200 group"
                  style={{ background: "var(--color-muted)" }}
                >
                  <Icon name={nav?.icon} size={18} color="var(--color-muted-foreground)" />
                  <span className="font-caption text-xs text-muted-foreground group-hover:text-foreground text-center leading-tight">{nav?.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
      {/* Trade Modal */}
      {tradeModal?.open && (
        <TradeModal
          holding={tradeModal?.holding}
          action={tradeModal?.action}
          onClose={() => setTradeModal({ open: false, holding: null, action: "buy" })}
          onConfirm={handleTradeConfirm}
        />
      )}
    </div>
  );
};

export default Portfolio;